/**
 * Куда вести пользователя после подтверждённой оплаты.
 *
 * Решение принимается по СОДЕРЖИМОМУ ПЛАТЕЖА (какой пакет реально оплачен), а
 * не по тому, какой маркер завалялся в localStorage. Маркеры ставятся ДО оплаты
 * и живут час, а пользователь мог передумать и купить совсем другое: раньше
 * ветку выбирали по маркерам, и покупка пакета РЕЛИЗОВ уносила на
 * `/generate-song`, где автозапуск съедал только что купленный баланс.
 *
 * Один хелпер на обе ветки возврата, чтобы решение не разъезжалось:
 *  • `/payment/success` в ТОЙ ЖЕ вкладке (миниапп / фолбэк при заблокированном
 *    попапе) — исполняет решение целиком;
 *  • главная вкладка веба (App: сигнал из вкладки оплаты) — исполняет всё,
 *    кроме `balance` (незачем выдёргивать пользователя со страницы, на которой
 *    он остался), а при релизе не трогает вкладку, если она И ТАК стоит на
 *    форме заявки — иначе увела бы с заполненного шага.
 */
import { isSongsPackage } from "@/shared/api/packages";
import { getPendingClipResumeNavigation } from "@/shared/lib/pendingClipAction";
import { getPendingReleaseResumePath } from "@/shared/lib/pendingReleaseOrder";
import { getPendingSongGeneration } from "@/shared/lib/pendingSongGeneration";
/**
 * Разбор оплаченного платежа в решение о навигации.
 *
 * Приоритет: заявка на релиз → клип-заказ → начатая генерация песни → «Баланс».
 */
export function resolvePaymentResume(payment) {
    // 1. Заявка на релиз — самый специфичный возврат: весь шаг мастера лежит в
    // URL, возвращаем ровно на экран подтверждения. Срабатывает, только если
    // оплачен пакет РЕЛИЗОВ (см. getPendingReleaseResumePath).
    const releasePath = getPendingReleaseResumePath(payment);
    if (releasePath) {
        return { kind: "release", path: releasePath };
    }
    // Дальше идут резюмы, которые ТРАТЯТ песенный баланс: и создание песни, и
    // клип-действия (видеообложка/караоке/продление) списываются с `songs`.
    // Пускаем их, только если оплачен пакет, который этот баланс начисляет —
    // иначе автозапуск потратил бы деньги, купленные совсем на другое.
    // Пакета в ответе нет (платёж без пакета / старый бэк) — тоже не рискуем:
    // цена ошибки здесь денежная, а «Баланс» ничего не тратит.
    const pkg = payment?.package;
    if (!pkg || !isSongsPackage(pkg)) {
        return { kind: "balance", path: "/balance" };
    }
    // 2. Клип-заказ приоритетнее песенного черновика — иначе после оплаты клипа
    // запустилась бы генерация песни.
    const clip = getPendingClipResumeNavigation();
    if (clip) {
        return { kind: "clip", path: clip.path, state: clip.state };
    }
    // 3. Пользователь нажал «Создать песню» и упёрся в пейвол — возвращаем на
    // экран настроек, где генерация продолжится. Ориентируемся ИМЕННО на маркер
    // намерения (ставится в обработчике 402), а не на черновик формы: черновик
    // означает лишь «есть текст», и по нему покупка пакета впрок запускала
    // генерацию без клика — см. pendingSongGeneration.
    if (getPendingSongGeneration() != null) {
        return {
            kind: "song",
            path: "/generate-song",
            state: { resumeSongSettings: true },
        };
    }
    return { kind: "balance", path: "/balance" };
}
